import { incotermRequiresAdjustments, isAdjustmentsRoute } from '../legacy-components/Adjustments/utils';
import { isFreshwaysEori } from './companies';
import { ValidateAllLoadAdjustments } from '../legacy-components/Util/ValidateLoadAdjustments';
import {
  getIsValidEuChed,
  getIsValidGbChed,
  getIsValidGmr,
  getIsValidJiraTicket,
  getIsValidMrn,
  getIsValidPbn,
  getIsValidPhilis,
} from './stringValidation';

const checkControlledGoods = (value) => {
  //check if store.controlledGoods[0] is 'N' or 'Y' or 'n' or 'y'. If that's the case, return true, otherwise return false
  if (value === 'N' || value === 'Y' || value === 'n' || value === 'y') return true;
  else return null;
};

const checkAdjustments = (adjustments) => {
  for (let adjustment of adjustments) {
    //we validate the adjustments based on the incoterm - if they're required and not filled, we show an error message.
    const adjustmentIncompleteValue = !parseFloat(adjustment.amount) || adjustment.currency === '';
    if (adjustment.required && adjustmentIncompleteValue) {
      return true;
    }
  }
  return false;
};

const isEmpty = (data) =>
  data === null ||
  data === undefined ||
  data === '' ||
  data === 0 ||
  (typeof data === 'object' && !Array.isArray(data) && Object.keys(data).length === 0);
export const containerValidation = (data) => {
  const containerRegex = /^[A-Za-z]{4}\d{7}$/;
  const isContainerInCorrectFormat = containerRegex.test(data);
  if (isContainerInCorrectFormat) return false;
  if (isEmpty(data)) return false;
  return 'Invalid container number. Leave blank or enter in format ABCD1234567';
};

export const jiraTicketValidation = (ticketName, isRequired) => {
  if (isRequired && !ticketName) {
    return 'Jira Ticket is mandatory for this Business Partner.';
  }

  return !ticketName || getIsValidJiraTicket(ticketName)
    ? ''
    : 'Jira ticket must be in format CCC-NUMBER (11 characters total, e.g. CCC-1234567)';
};

export const poValidation = (poNumber, parentEori) => {
  if (isFreshwaysEori(parentEori)) {
    return isEmpty(poNumber) ? 'Please check CCC ticket is valid for Freshways' : '';
  }
  return '';
};

/**
 * Normalises boolean values from API responses (handles true, 1, 'true', '1', 'True')
 */
export const normaliseBoolean = (value) => {
  return value === true || value === 1 || String(value).toLowerCase() === 'true' || value === '1';
};

/**
 * Determines if a subtask can be deleted for EPO jobs
 *
 * Rules:
 * - Only subtasks from subtask modal can be deleted (Scenario 3)
 * - Customer-requested subtasks cannot be deleted (Scenario 4)
 * - Allowed types: CHED/IPAFFS, PHILIS, GMR, PBN, TAD, ENS, EXS, ADMIN FEE
 */

export const headerContainsErrors = (store, qaStore, currentTab) => {
  const entryPortRequired = !store.RowExport[0];
  const exitPortRequired = store.ffd[0] === false && !store.RowImport[0];
  const controlledGoodsRequired = store.ffd[0] === false && store.GBimport[0];
  const portModelRequired =
    (store.GBimport[0] || store.GBexport[0]) &&
    (store.importMixedPortsModal[0] === 1 || store.exportMixedPortsModal[0] === 1);
  const isAdjustmentRequired =
    isAdjustmentsRoute(store.directionSelected[0]) && incotermRequiresAdjustments(store.selectincoterm[0]?.toString());
  const allAdjustmentsAreValid = ValidateAllLoadAdjustments(store.adjustments[0]);
  const parentEori = store.newLoadParent[0]?.parentEori;

  const isMultipleConsignors = store.multipleConsignors[0];
  const isMultipleInvoices = store.multipleInvoices[0];

  const fields = [
    {
      field: 'container',
      name: 'Container',
      hasError: containerValidation(store.container[0]),
    },
    {
      field: 'directionSelected',
      name: 'Movement Type',
      hasError: isEmpty(store.directionSelected[0]),
    },
    {
      field: 'selectFcl',
      name: 'Full Load or Groupage',
      hasError: isEmpty(store.selectFcl[0]),
    },
    {
      field: 'despatch',
      name: 'Country of Despatch',
      hasError: isEmpty(store.despatch[0]),
    },
    {
      field: 'destination',
      name: 'Country of Destination',
      hasError: isEmpty(store.destination[0]),
    },
    ...(entryPortRequired
      ? [
          {
            field: 'entry',
            name: 'Entry Port',
            hasError: isEmpty(store.entry[0]),
          },
        ]
      : []),
    ...(exitPortRequired
      ? [
          {
            field: 'exit',
            name: 'Exit Port',
            hasError: isEmpty(store.exit[0]),
          },
        ]
      : []),
    ...(portModelRequired
      ? [
          {
            field: 'portsModal',
            name: 'Port Model',
            hasError: isEmpty(store.portsModal[0]),
          },
        ]
      : []),
    {
      field: 'consignor',
      name: 'Consignor Details',
      hasError:
        (isEmpty(currentTab === 3 ? qaStore?.consignorData?.[0] : store?.consignor[0]) && !isMultipleConsignors) ||
        (isMultipleConsignors && !store.isMultipleConsignorsValid),
    },
    {
      field: 'consignee',
      name: 'Consignee Details',
      hasError: isEmpty(currentTab === 3 ? qaStore?.consigneeData?.[0] : store?.consignee[0]),
    },
    {
      field: 'shipStatus',
      name: 'Shipping Status',
      hasError: isEmpty(store.shipStatus[0]),
    },
    {
      field: 'truck',
      name: 'Truck / Trailer Number',
      hasError: isEmpty(store.truck[0]),
    },
    {
      field: 'date',
      name: 'Date of Loading',
      hasError: !isValidDate(store.date[0]),
    },
    {
      field: 'jiraTicket',
      name: 'Jira Ticket',
      hasError: jiraTicketValidation(store.jiraTicket[0], store.isJiraTicketRequired[0]),
    },
    {
      field: 'jobRef',
      name: 'Job Reference',
      hasError: isEmpty(store.jobRef[0]) || (store.jobRef[0]?.length ?? 0) > 35,
    },
    {
      field: 'invoicenumber',
      name: 'Invoice Number',
      hasError: isEmpty(store.invoicenumber[0]) && !isMultipleInvoices,
    },
    {
      field: 'poNumber',
      name: 'PO Number',
      hasError: poValidation(store.poNumber[0], parentEori),
    },
    {
      field: 'invoicecurr',
      name: 'Invoice Currency',
      hasError: isEmpty(store.invoicecurr[0]),
    },
    {
      field: 'selectincoterm',
      name: 'Incoterm',
      hasError: isEmpty(store.selectincoterm[0]),
    },
    ...(controlledGoodsRequired
      ? [
          {
            field: 'controlledGoods',
            name: 'Controlled Goods',
            hasError: !checkControlledGoods(store.controlledGoods[0]),
          },
        ]
      : []),
    ...(store.directionSelected[0] === 1
      ? [
          {
            field: 'entry2',
            name: 'Entry Port 2',
            hasError: isEmpty(store.entry2[0]),
          },
          {
            field: 'exit2',
            name: 'Exit Port 2',
            hasError: isEmpty(store.exit2[0]),
          },
        ]
      : []),
    ...(parseInt(store.selectincoterm[0]) === 7 && store.GBimport[0]
      ? [
          {
            field: 'incotermLocation',
            name: 'Incoterm Location',
            hasError: isEmpty(store.incotermLocation[0]),
          },
        ]
      : []),
    ...(isAdjustmentRequired || !allAdjustmentsAreValid
      ? [
          {
            field: 'adjustments',
            name: 'Adjustments (Freight Charges)',
            hasError: checkAdjustments(store.adjustments[0]),
          },
        ]
      : []),
  ];
  return {
    hasErrors: fields.some((field) => field.hasError),
    fieldsWithErrors: fields.filter((field) => field.hasError),
  };
};

export const validateSystemReferenceFromStatus = (item) => {
  const formType = item['Form type'];
  const additionalInformation = item['Additional Information'];
  const { EAD, TAD, ENS, System } = item;
  const text = item['System Ref No.'];

  if (
    (additionalInformation === 'IE Import' || additionalInformation === 'IE Export') &&
    (formType === 'Import declaration in' || formType === 'Export declaration (incl. EXS) in')
  ) {
    const isValid = getIsValidMrn(text, 'IE', false) || !text;
    return isValid ? '' : 'Must be a valid IE MRN (e.g. 26IEDUB10056OG3AB5)';
  }

  if (
    EAD ||
    TAD ||
    formType === 'Import declaration in' ||
    formType === 'Export declaration (incl. EXS) in' ||
    additionalInformation === 'EXS'
  ) {
    const isValid = getIsValidMrn(text, 'GB', false) || !text;
    return isValid ? '' : 'Must be a valid GB MRN (e.g. 26GB1234567890ABCD)';
  }

  if (ENS) {
    const isValid = getIsValidMrn(text, ['GB', 'IE', 'FR'], false) || !text;
    return isValid ? '' : 'Must be a valid GB IE or FR MRN (e.g. 26FR1234567890ABCD)';
  }

  if (additionalInformation === 'IPAFFS') {
    const isValid = getIsValidGbChed(text) || !text;
    return isValid ? '' : 'Must be a valid GB CHED (e.g. CHEDP.GB.2026.1234567)';
  }

  if (additionalInformation === 'CHED') {
    const isValid = getIsValidEuChed(text) || !text;
    return isValid ? '' : 'Must be a valid CHED (e.g. CHEDP.IE.2026.1234567)';
  }

  if (additionalInformation === 'GMR') {
    const isValid = getIsValidGmr(text) || !text;
    return isValid ? '' : 'Must start with GMR then 9 alphanumeric characters';
  }

  if (additionalInformation === 'PBN') {
    const isValid = getIsValidPbn(text) || !text;
    return isValid ? '' : 'Must be 8 alphanumeric characters';
  }

  if (System === 'PHILIS') {
    const isValid = getIsValidPhilis(text) || !text;
    return isValid ? '' : 'Must be a valid PHILIS ref e.g IND2.GB.2026.0001234';
  }
};

export const systemReferenceHasAnyStatusErrors = (localData) => {
  return localData.some((item) => {
    return !item.locked && !!validateSystemReferenceFromStatus(item);
  });
};

export const isGBImport = (id) => {
  switch (id) {
    case 2:
    case '2':
    case 10:
    case '10':
    case 11:
    case '11':
    case 13:
    case '13':
      return true;
    default:
      return false;
  }
};

export const isGBexport = (id) => {
  switch (id) {
    case 3:
    case 4:
    case 5:
    case 6:
    case 7:
    case 12:
    case '3':
    case '4':
    case '5':
    case '6':
    case '7':
    case '12':
      return true;
    default:
      return false;
  }
};

export const isValidDate = (date) => {
  if (!date) return false;

  // Force local parsing
  const d = new Date(date + 'T00:00:00');

  if (isNaN(d)) return false;

  const now = new Date();
  const diff = Math.abs(now - d);
  const max = 365 * 24 * 60 * 60 * 1000;

  return diff <= max;
};
